/**
 * Matterlane automation marker — Viberary vendored copy.
 *
 * Canonical definition: matterlane-engineering
 * automation/packages/browser/src/marker.ts. Vendored rather than imported
 * because distribution is a tag-pinned source checkout and this app has no
 * build-time dependency on the private package. MARKER_TEST_VECTORS below is
 * copied verbatim from the canonical module so the two cannot drift apart;
 * matterlaneAutomation.spec runs every vector.
 *
 * SDR-2026-005: site-side suppression keys on the COOKIE. A request header is
 * not observable from page JavaScript; document.cookie is.
 *
 * The predicate is total, pure, deterministic and FAIL-OPEN, where "open" means
 * open to the existing normal path. Anything that is not an exact, positively
 * established marker returns false, and a thrown error also returns false, so
 * the caller runs its ordinary analytics/advertising code.
 *
 * The worst credible failure of this function is that an automation session is
 * counted as a real visitor. It can never cause a real visitor to be treated as
 * automation, which is what the accepted priority order requires.
 */

export const MARKER_COOKIE_NAME = 'matterlane_automation';

/**
 * Evaluated per call, never cached at module load, so a context that acquires
 * or loses the marker mid-session is always judged on current state.
 */
export function isAutomationContext(cookieHeader) {
  try {
    if (typeof cookieHeader !== 'string' || cookieHeader.length === 0) return false;
    return /(?:^|;)\s*matterlane_automation=1\s*(?:;|$)/.test(cookieHeader);
  } catch (error) {
    return false;
  }
}

/** Reads live document.cookie on every call. Any failure takes the normal path. */
export function matterlaneAutomationDetected() {
  try {
    return isAutomationContext(typeof document !== 'undefined' ? document.cookie : undefined) === true;
  } catch (error) {
    return false;
  }
}

/** Conformance vectors, copied verbatim from the canonical marker module. */
export const MARKER_TEST_VECTORS = [
  { input: 'matterlane_automation=1', expected: true },
  { input: 'a=b; matterlane_automation=1', expected: true },
  { input: 'matterlane_automation=1; a=b', expected: true },
  { input: 'a=b; matterlane_automation=1; c=d', expected: true },
  { input: 'a=b;matterlane_automation=1;c=d', expected: true },
  { input: ' matterlane_automation=1 ', expected: true },

  { input: '', expected: false },
  { input: 'a=b', expected: false },
  { input: 'matterlane_automation=', expected: false },
  { input: 'matterlane_automation=0', expected: false },
  { input: 'matterlane_automation=2', expected: false },
  { input: 'matterlane_automation=10', expected: false },
  { input: 'matterlane_automation=1x', expected: false },
  { input: 'matterlane_automation=true', expected: false },
  { input: 'xmatterlane_automation=1', expected: false },
  { input: 'not_matterlane_automation=1', expected: false },
  { input: 'MATTERLANE_AUTOMATION=1', expected: false },
  { input: 'matterlane_automation =1', expected: false },

  { input: undefined, expected: false },
  { input: null, expected: false },
  { input: 1, expected: false },
  { input: {}, expected: false },
  { input: [], expected: false }
];
